import React, { useCallback, useState, useEffect, useMemo } from "react";
import images from "../../assets/images";
import "../../index.css";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ValidationModule,
  themeQuartz,
} from "ag-grid-community";
import { Link } from "react-router-dom";
import axiosInstance from "../../common/axiosInstance";
import { premisesApiResponse, RowData } from "../../types/api";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { getPremisesPage } from "../../service/api";
import { useServerPagination } from "../../hooks/useServerPagination";
import { PaginationFooter } from "../../components/common/PaginationFooter";
import { formatDate } from "../../common/utils";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
]);

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});


function toRow(item: premisesApiResponse, regionName: string): RowData {
  return {
    id: item.id || item._id || "",
    premisesName: item.premisesName || "—",
    addressLine1: item.addressLine1 || "—",
    city: item.townCity || "—",
    region: regionName,
    lastUpdate: formatDate(item.updatedAt || item.lastUpdated || item.createdAt || item.createdDate),
    status: Boolean(item.isStatus),
    action: null,
  };
}

const Premises: React.FC = () => {
  const [rowData, setRowData] = useState<RowData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");

  const {
    page,
    pageSize,
    total,
    setPage,
    setTotal,
    resetPage,
    changePageSize,
  } = useServerPagination({ initialPageSize: 20 });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadPremises = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await getPremisesPage({
        page,
        pageSize,
        ...(search ? { search } : {}),
        ...(selectedRegion ? { regionId: selectedRegion } : {}),
        ...(selectedPremises ? { premiseId: selectedPremises } : {}),
      });

      const payload = response.data;

      if (!payload.success || !Array.isArray(payload.data)) {
        throw new Error("Invalid premises list response");
      }

      const results = payload.data;

      setRowData(
        results.map((item) =>
          toRow(item, item.regionName || "N/A"),
        ),
      );
      const pagination = payload.meta?.pagination;
      setTotal(Number(pagination?.total ?? results.length));
    } catch (error) {
      console.error("Error fetching premises list:", error);
      setErrorMessage("Unable to load premises. Please try again.");
      setRowData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, selectedRegion, selectedPremises]);

  useEffect(() => {
    void loadPremises();
  }, [loadPremises]);

  const toggleStatus = useCallback(async (id: string, status: boolean) => {
    try {
      await axiosInstance.put(`/api/premises/toggle-status/${id}`, {
        isStatus: !status,
      });
      await loadPremises();
    } catch (error) {
      console.error("Error toggling premise status:", error);
      setErrorMessage("Unable to update premise status. Please try again.");
    }
  }, [loadPremises]);

  const columnDefs = useMemo(
    () => [
      {
        field: "premisesName",
        headerName: "Premise Name",
        flex: 1.5,
        minWidth: 150,
        tooltipField: "premisesName",
      },
      {
        field: "addressLine1",
        headerName: "Address",
        minWidth: 180,
        flex: 1,
      },
      { field: "city", headerName: "City", minWidth: 140, flex: 1 },
      { field: "region", headerName: "Region", minWidth: 110, flex: 1 },
      { field: "lastUpdate", headerName: "Last Updated", minWidth: 130, flex: 1 },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        cellRenderer: (params: any) => {
          const isActive = params.data.status === true;
          return (
            <button
              type="button"
              aria-label={`Set ${params.data.premisesName} ${isActive ? "inactive" : "active"}`}
              onClick={() => void toggleStatus(params.data.id, isActive)}
              className={`rounded-full px-3 py-1 text-sm font-medium ${isActive ? "bg-teal-100 text-teal-800" : "bg-red-100 text-red-800"
                }`}
            >
              {isActive ? "Active" : "Inactive"}
            </button>
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        minWidth: 100,
        cellRenderer: (params: any) => (
          <div className="flex gap-3 pt-2">
            <Link to={`/view-premises/${params.data.id}`} aria-label="View premise">
              <img className="iconTd" src={images.viewIcon} alt="View" />
            </Link>
            <Link to={`/update-premises/${params.data.id}`} aria-label="Edit premise">
              <img className="iconTd" src={images.editIcon} alt="Edit" />
            </Link>
          </div>
        ),
      },
    ],
    [toggleStatus],
  );

  const defaultColDef = useMemo(() => ({
    editable: false, sortable: false, filter: false,
    resizable: true,

    // Share available width between columns.
    flex: 1,
    minWidth: 90,

    // AG Grid column properties, not CSS styles.
    // only enable if you need multi-line content in specific columns..
    wrapText: false,
    autoHeight: false,

    cellStyle: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      cursor: "pointer",
      padding: "8px 8px",
      // textAlign: "center",
      lineHeight: "1.5",
    },

    headerClass: "font-bold text-md p-2",  //default bg colorr..

    tooltipValueGetter: (params: any) =>
      params.value == null ? "" : String(params.value),
  }), []);


  return (
    <div className="w-full px-3">
      <div className="page-header mt-20">
        <h2 className="header-title ml-2">Premises List</h2>

        <div className="dashHeaderRight">
          <Link
            to="/add-premises"
            className="flex btn-primary roundedBt iconWithBt text-uppercase"
          >
            <img src={images.addPremiessIcon} alt="" />
            <span>ADD PREMISES</span>
          </Link>
        </div>
      </div>

      <div className="tableBlock w-full">
        <div className="card">
          <div className="flex flex-wrap items-center justify-between gap-3 p-3">
            <label className="min-w-[180px] flex-1 mt-2">
              <span className="sr-only">Search premises</span>
              <input
                type="search"
                className="form-control w-full"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value)
                  resetPage();
                }}
                placeholder="Search premises..."
              />
            </label>
            <div className="flex flex-wrap gap-3">
              <Dropdown
                type="region"
                selectedValue={selectedRegion}
                onChange={(value) => {
                  setSelectedRegion(value);
                  setSelectedPremises("");
                  setPage(1);
                }}
              />
              <Dropdown
                type="premises"
                selectedValue={selectedPremises}
                selectedRegion={selectedRegion}
                onChange={(value) => {
                  setSelectedPremises(value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {errorMessage && (
            <div role="alert" className="mx-3 mb-3 rounded bg-red-50 p-3 text-sm text-red-700">
              {errorMessage}
              <button type="button" className="ml-3 underline" onClick={() => void loadPremises()}>
                Retry
              </button>
            </div>
          )}
          <div className="ag-theme-alpine w-full min-w-0 min-h-[200px] pl-2 border-0 rounded-lg">
            {isLoading ? (
              <div className="flex min-h-[200px] items-center justify-center" role="status" aria-label="Loading premises">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                {/* Loading premises.../ */}
              </div>
            ) : rowData.length ? (
              <>
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs as any}
                  theme={gridTheme}
                  headerHeight={50}
                  rowHeight={50}
                  domLayout='autoHeight'
                  defaultColDef={defaultColDef}
                />
                <PaginationFooter
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  onPageChange={setPage}
                  onPageSizeChange={changePageSize}
                />
              </>
            ) : (
              <div className="flex min-h-[200px] my-5 items-center justify-center text-gray-500">
                {errorMessage || "No premises found."}
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Premises;
